import { useNavigate } from "react-router-dom";
import { useCallback, useState } from "react";
import ModifyTaskStateModal from "../_dashboard/tareas/ModifyTaskStateModal";
import CantidadRemuneracionesDialog from "../tareas/CantidadRemuneracionesDialog";
import SolicitarCambioEstadoDialog from "../tareas/SolicitarCambioEstadoDialog";
import { formatDate } from "src/utils/converter";
import { tareasApi } from "src/services/tareasApi";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { NotificationImportant, Update } from "@material-ui/icons";
import { useSnackbar } from "src/components/snackbar";

const TIPO_TAREA_COLORS = {
  AÑO: "#1976d2",
  INTERNA: "#d32f2f",
  MES: "#ed6c02",
  ESPECIAL: "#0288d1",
};

const ESTADO_CHIP_CONFIG = {
  TERMINADA: { label: "Terminada", color: "success" },
  FACTURADO: { label: "Facturado", color: "info" },
  "PENDIENTE DE FACTURACION": { label: "Pendiente Fact.", color: "warning" },
  "CANCELADO O RECHAZADO": { label: "Cancelado", color: "error" },
  "OBSERVADA SII": { label: "Observada SII", color: "error" },
};

export default function TareasKanban({ tareas }) {
  const navigate = useNavigate();
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModifyTaskState, setShowModifyTaskState] = useState(false);
  const [showCantidadRemuneraciones, setShowCantidadRemuneraciones] =
    useState(false);
  const [actualizarEstadoTarea] = tareasApi.useUpdateCambiaEstadoMutation();
  const { rol: rolObtenido } = useSelector((state) => state.auth.user);
  const [mostrarSolicitarCambioEstado, setMostrarSolicitarCambioEstado] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [guardandoDatos, setGuardandoDatos] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const esAdmin = rolObtenido === "SUPER_ADMIN" || rolObtenido === "ADMIN";

  const handleChangeTaskState = (tarea, option = "admin") => {
    setSelectedTask(tarea);
    if (option === "admin") {
      setSelectedTaskId(tarea.id);
      setIsLoading(true);
      setShowModifyTaskState(true);
    } else {
      setMostrarSolicitarCambioEstado(true);
    }
  };

  const updateTaskInDB = useCallback(
    async (modificacionEstado) => {
      try {
        await actualizarEstadoTarea({
          id: modificacionEstado.id,
          body: {
            estado: modificacionEstado.estado,
            fechaTermino: modificacionEstado.fechaTermino,
          },
        }).unwrap();
        enqueueSnackbar("¡Tarea actualizada correctamente!", {
          variant: "success",
        });
      } catch (error) {
        enqueueSnackbar("Ocurrió un error al actualizar", { variant: "error" });
      } finally {
        setSelectedTask(null);
        setSelectedTaskId(null);
        setIsLoading(false);
      }
    },
    [actualizarEstadoTarea, enqueueSnackbar],
  );

  const doChangeTaskState = useCallback(
    async (newState) => {
      if (selectedTask.estado === newState)
        return setShowModifyTaskState(false);

      let modificacionEstado = {
        ...selectedTask,
        estado: newState,
      };

      if (
        newState === "TERMINADA" &&
        (selectedTask.tipoTarea === "MES" ||
          selectedTask.tipoTarea === "AÑO") &&
        String(selectedTask.nombre)
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .includes("REMUNERACION")
      ) {
        setSelectedTask(modificacionEstado);
        setShowModifyTaskState(false);
        return setShowCantidadRemuneraciones(true);
      }

      if (newState === "TERMINADA") {
        if (selectedTask.tipoTarea === "ESPECIAL") {
          modificacionEstado.estado = "PENDIENTE DE FACTURACION";
        }
        modificacionEstado.fechaTermino = getToday();
      } else {
        modificacionEstado.fechaTermino = "";
      }

      setShowModifyTaskState(false);
      await updateTaskInDB(modificacionEstado);
    },
    [selectedTask, updateTaskInDB],
  );

  const updateTaskInDBRemuneraciones = useCallback(
    async (modificacionEstado) => {
      try {
        await actualizarEstadoTarea({
          id: modificacionEstado.id,
          body: {
            estado: modificacionEstado.estado,
            fechaTermino: modificacionEstado.fechaTermino,
            cantidadRemuneraciones: modificacionEstado.cantidadFactura,
          },
        }).unwrap();

        enqueueSnackbar("¡Tarea actualizada correctamente!", {
          variant: "success",
        });
      } catch (error) {
        enqueueSnackbar("Ocurrió un error al actualizar", { variant: "error" });
      } finally {
        setSelectedTask(null);
        setGuardandoDatos(false);
      }
    },
    [actualizarEstadoTarea, enqueueSnackbar],
  );

  const getToday = () => {
    const today = new Date();
    const day = today.getDate();
    const month = today.getMonth() + 1;
    const year = today.getUTCFullYear();
    return formatDate(`${year}/${month}/${day}`);
  };

  const getBorderColor = (tipo) => TIPO_TAREA_COLORS[tipo] || "transparent";

  if (!tareas || tareas.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "center", py: 4 }}
      >
        No posee tareas
      </Typography>
    );
  }

  return (
    <>
      {tareas.map((tarea) => {
        const borderColor = getBorderColor(tarea.tipoTarea);
        const estadoConfig = ESTADO_CHIP_CONFIG[tarea.estado];
        const cargandoEstaTarea = isLoading && selectedTaskId === tarea.id;

        return (
          <Card
            key={tarea.id}
            sx={{
              mb: 1.5,
              borderLeft: `4px solid ${borderColor}`,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 3,
              },
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  cursor: "pointer",
                  fontWeight: 600,
                  wordBreak: "break-word",
                  "&:hover": { color: "primary.main" },
                }}
                onClick={() => navigate(`/dashboard/tareas/${tarea.id}`)}
              >
                {tarea.nombre.charAt(0).toUpperCase() +
                  tarea.nombre.slice(1).toLowerCase()}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                Cliente:{" "}
                {tarea?.nombreCliente.charAt(0).toUpperCase() +
                  tarea?.nombreCliente.slice(1).toLowerCase()}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={
                      tarea?.tipoTarea.charAt(0) +
                      tarea?.tipoTarea.slice(1).toLowerCase()
                    }
                    size="small"
                    sx={{
                      bgcolor: `${borderColor}20`,
                      color: borderColor,
                      fontWeight: 500,
                      fontSize: "0.7rem",
                    }}
                  />

                  {estadoConfig && (
                    <Chip
                      label={estadoConfig.label}
                      size="small"
                      color={estadoConfig.color}
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  )}
                </Box>

                <Tooltip
                  title={
                    esAdmin ? "Cambiar estado" : "Solicitar cambio de estado"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleChangeTaskState(
                        tarea,
                        esAdmin ? "admin" : "employee",
                      )
                    }
                    disabled={cargandoEstaTarea}
                  >
                    {cargandoEstaTarea ? (
                      <CircularProgress size={20} />
                    ) : esAdmin ? (
                      <Update fontSize="small" />
                    ) : (
                      <NotificationImportant fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Card>
        );
      })}

      {selectedTask && (
        <ModifyTaskStateModal
          task={selectedTask}
          onSubmit={doChangeTaskState}
          onClose={() => {
            setShowModifyTaskState(false);
            setIsLoading(false);
          }}
          states={[
            "PENDIENTE",
            "EN PROCESO",
            "OBSERVADA SII",
            "TERMINADA",
            "FACTURADO",
            "CANCELADO O RECHAZADO",
          ]}
          open={showModifyTaskState}
          cargandoDatos={false}
        />
      )}

      {selectedTask && showCantidadRemuneraciones && (
        <CantidadRemuneracionesDialog
          open={showCantidadRemuneraciones}
          setOpen={setShowCantidadRemuneraciones}
          tarea={selectedTask}
          updateTaskInDBRemuneraciones={updateTaskInDBRemuneraciones}
          guardandoDatos={guardandoDatos}
          setGuardandoDatos={setGuardandoDatos}
        />
      )}

      {mostrarSolicitarCambioEstado && (
        <SolicitarCambioEstadoDialog
          open={mostrarSolicitarCambioEstado}
          setOpen={setMostrarSolicitarCambioEstado}
          tarea={selectedTask}
        />
      )}
    </>
  );
}
